// Iter-200 — Admin queue for client-initiated payment declarations.
// Lists every declaration with quick Validate / Reject actions on
// PENDING_CEO items. Auto-paid declarations are shown for traceability.
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, X, RefreshCw, ExternalLink, Banknote, Paperclip, User, Globe } from "lucide-react";

const STATUS_COLORS = {
  PENDING_CEO: "bg-amber-100 text-amber-800 border-amber-200",
  AUTO_PAID: "bg-emerald-100 text-emerald-800 border-emerald-200",
  VALIDATED: "bg-blue-100 text-blue-800 border-blue-200",
  REJECTED: "bg-red-100 text-red-800 border-red-200",
};

const fmt = (n, ccy) =>
  `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy || ""}`;

export default function PaymentDeclarations() {
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState("PENDING_CEO");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/admin/payment-declarations${q}`);
      setRows(r.data.declarations || []);
    } catch (e) {
      toast.error("Impossible de charger les déclarations");
    } finally {
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const validate = async (decl) => {
    if (!window.confirm(`Valider la déclaration et marquer la facture ${decl.invoice_number} comme réglée ?`)) return;
    try {
      await api.post(`/admin/payment-declarations/${decl.declaration_id}/validate`, {});
      toast.success("Déclaration validée. Facture marquée payée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation échouée");
    }
  };

  const reject = async (decl) => {
    const reason = window.prompt("Raison du rejet (ex: pas trouvé sur extrait bancaire) :");
    if (!reason || reason.trim().length < 3) {
      toast.error("Une raison d'au moins 3 caractères est requise");
      return;
    }
    try {
      await api.post(`/admin/payment-declarations/${decl.declaration_id}/reject`, { reason: reason.trim() });
      toast.success("Déclaration rejetée. La facture reste impayée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejet échoué");
    }
  };

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto" data-testid="payment-declarations-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl text-zinc-900 flex items-center gap-2">
            <Banknote size={22} className="text-zinc-700" />
            Déclarations de paiement client
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-3xl">
            Vos clients peuvent déclarer un paiement depuis chaque email de facture / relance via un lien sécurisé. Les déclarations dont le montant correspond exactement à la facture sont marquées auto-réglées. Les autres atterrissent ici pour validation.
          </p>
        </div>
        <button onClick={load} disabled={refreshing} data-testid="pd-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-300 rounded-md text-sm hover:bg-zinc-50 disabled:opacity-50">
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Refresh
        </button>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2 mb-4 text-xs">
        {[
          { v: "PENDING_CEO", label: "À valider", color: "amber" },
          { v: "AUTO_PAID", label: "Auto-réglées", color: "emerald" },
          { v: "VALIDATED", label: "Validées", color: "blue" },
          { v: "REJECTED", label: "Rejetées", color: "red" },
          { v: "", label: "Toutes", color: "zinc" },
        ].map((s) => (
          <button
            key={s.v}
            onClick={() => setStatusFilter(s.v)}
            data-testid={`pd-filter-${s.v || "all"}`}
            className={`px-3 h-8 rounded-full border ${
              statusFilter === s.v
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Rows */}
      {rows === null ? (
        <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="text-zinc-500 text-sm py-12 text-center border border-dashed border-zinc-300 rounded-lg">
          Aucune déclaration dans ce filtre.
        </div>
      ) : (
        <div className="overflow-x-auto border border-zinc-200 rounded-lg bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Date déclaration</th>
                <th className="text-left px-3 py-2">Facture</th>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-right px-3 py-2">Montant déclaré</th>
                <th className="text-right px-3 py-2">Attendu</th>
                <th className="text-left px-3 py-2">Mode / Réf.</th>
                <th className="text-left px-3 py-2">Statut</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const apiBase = process.env.REACT_APP_BACKEND_URL;
                const hasProof = !!d.proof_file;
                const proofUrl = hasProof ? (d.proof_file.drive_url || `${apiBase}/api/admin/payment-declarations/${d.declaration_id}/proof`) : null;
                return (
                <tr key={d.declaration_id} className="border-t border-zinc-100 align-top" data-testid={`pd-row-${d.declaration_id}`}>
                  <td className="px-3 py-2 text-zinc-600 font-mono text-xs whitespace-nowrap">{d.declared_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 font-medium">{d.invoice_number}</td>
                  <td className="px-3 py-2 text-zinc-700">
                    <div>{d.client_name}</div>
                    {(d.declarant_first_name || d.declarant_email) && (
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1" data-testid={`pd-declarant-${d.declaration_id}`}>
                        <User size={10} /> {d.declarant_first_name} {d.declarant_last_name}
                        {d.declarant_email && <span className="ml-1">· <a href={`mailto:${d.declarant_email}`} className="underline">{d.declarant_email}</a></span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">{fmt(d.declared_amount, d.invoice_currency)}</td>
                  <td className="px-3 py-2 text-right text-zinc-500 tabular-nums">{fmt(d.expected_amount, d.invoice_currency)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="text-zinc-700">{d.payment_method}</div>
                    <div className="text-zinc-500 font-mono">{d.payment_reference}</div>
                    {d.comment && <div className="text-zinc-500 italic mt-0.5">« {d.comment} »</div>}
                    {hasProof && (
                      <a href={proofUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 text-[11px] text-blue-700 underline hover:no-underline"
                        data-testid={`pd-proof-${d.declaration_id}`}>
                        <Paperclip size={10} /> Pièce jointe ({d.proof_file.filename?.slice(-30)})
                      </a>
                    )}
                    {d.proof_text && (
                      <div className="mt-1 text-[10px] text-zinc-600 italic max-w-xs whitespace-pre-wrap" title={d.proof_text}>
                        📋 {d.proof_text.slice(0, 80)}{d.proof_text.length > 80 ? "…" : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLORS[d.status] || ""}`}>
                      {d.status === "PENDING_CEO" ? "À VALIDER" : d.status === "AUTO_PAID" ? "AUTO-RÉGLÉE" : d.status}
                    </span>
                    {d.rejection_reason && <div className="text-[11px] text-red-700 mt-1">« {d.rejection_reason} »</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {d.status === "PENDING_CEO" ? (
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => validate(d)} data-testid={`pd-validate-${d.declaration_id}`}
                          className="inline-flex items-center gap-1 px-2 h-7 bg-emerald-600 text-white rounded text-[11px] hover:bg-emerald-700">
                          <Check size={11} /> Valider
                        </button>
                        <button onClick={() => reject(d)} data-testid={`pd-reject-${d.declaration_id}`}
                          className="inline-flex items-center gap-1 px-2 h-7 bg-zinc-100 border border-zinc-300 rounded text-[11px] hover:bg-zinc-200">
                          <X size={11} /> Rejeter
                        </button>
                      </div>
                    ) : (
                      <a href={`/invoices?focus=${d.invoice_id}`} className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900">
                        <ExternalLink size={11} /> Voir facture
                      </a>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
